import { useTranslation } from 'react-i18next';
import { type User } from '@bliks-io/contracts';
import { FieldSeparator } from '@bliks-io/ui';
import { StatusActionAlert } from './StatusActionAlert';
import { STATUS_ACTIONS_CONFIG } from './org-member-status-config';

interface OrgMemberStatusActionsProps {
  member: User;
  onSendInvitation?: (memberId: string) => void | Promise<void>;
  onUnsuspend?: (memberId: string) => void | Promise<void>;
  onSuspend?: (memberId: string) => void | Promise<void>;
  isSendingInvitation?: boolean;
  isUnsuspending?: boolean;
  isSuspending?: boolean;
}

/**
 * Renders status-specific actions for organization members (edit mode only)
 */
export function OrgMemberStatusActions({
  member,
  onSendInvitation,
  onUnsuspend,
  onSuspend,
  isSendingInvitation = false,
  isUnsuspending = false,
  isSuspending = false
}: OrgMemberStatusActionsProps) {
  const { t } = useTranslation();

  const configs = STATUS_ACTIONS_CONFIG[member.status];
  if (!configs || configs.length === 0) return null;

  const handlers = {
    onSendInvitation,
    onUnsuspend,
    onSuspend
  };

  const loadingStates = {
    isSendingInvitation,
    isUnsuspending,
    isSuspending
  };

  return (
    <div className="mt-8 flex flex-col gap-4">
      <FieldSeparator>{t('organization.members.statusActions')}</FieldSeparator>
      <div className="flex flex-col gap-3">
        {configs.map((config, index) => {
          const handler = handlers[config.handler];
          const loading = loadingStates[config.loadingProp];

          if (!handler) return null;

          return (
            <StatusActionAlert
              key={index}
              icon={config.icon}
              message={t(config.messageKey)}
              buttonLabel={t(config.buttonLabelKey)}
              onAction={() => handler(member._id)}
              loading={loading}
              variant={config.alertVariant}
              buttonVariant={config.buttonVariant}
            />
          );
        })}
      </div>
    </div>
  );
}
